<template>
	<div>
		<div class="stage">
			<page-header title="File Browser" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="false"
				:has-view-toggle="true"
				:view-mode="viewMode"
				@toggle-filters="showFilters = !showFilters"
				@toggle-view="toggleViewMode"
				@reset-filters="resetSearchParams"
			>
				<template v-if="$global.hasUserRole(userRoles, 'files:create')" #custom-buttons>
					<div class="flex w-full flex-wrap items-end justify-end gap-4 pl-2 pt-2 lg:pt-0 xl:w-auto">
						<button class="group flex items-center gap-2" @click="isUploadOpen = true">
							<div class="fabLabel">
								<p>Upload File</p>
							</div>
							<div
								class="fab h-10 w-10 min-w-10 group-hover:bg-green-600 group-active:bg-green-700 sm:h-12 sm:w-12 sm:min-w-12"
							>
								<span class="fa-light fa-plus"></span>
							</div>
						</button>
						<upload-file-audio
							:selected-tenant-id="searchParams.selectedTenant"
							:disable-overlay-close="true"
							:open="isUploadOpen"
							@close="closeAndReload"
						/>
						<button class="group flex items-center gap-2" @click="openBulkUpload">
							<div class="fabLabel">
								<p>Bulk Upload</p>
							</div>
							<div
								class="fab orange h-10 w-10 min-w-10 border-0 group-hover:bg-orange-600 group-active:bg-orange-700 sm:h-12 sm:w-12 sm:min-w-12"
							>
								<span class="fa-light fa-file-import"></span>
							</div>
						</button>
						<button class="group flex items-center gap-2" @click="isRecordOpen = true">
							<div class="fabLabel">
								<p>Record Audio</p>
							</div>
							<div
								class="fab red h-10 w-10 min-w-10 group-hover:bg-red-600 group-active:bg-red-700 sm:h-12 sm:w-12 sm:min-w-12"
							>
								<span class="fa-light fa-circle"></span>
							</div>
						</button>
						<upload-record-audio
							v-if="isRecordOpen"
							:selected-tenant-id="searchParams.selectedTenant"
							:disable-overlay-close="true"
							:open="true"
							@close="closeAndReload"
						/>
						<upload-file-bulk-modal
							v-if="isBulkSettingsOpen"
							:selected-tenant-id="searchParams.selectedTenant"
							:disable-overlay-close="true"
							:open="isBulkSettingsOpen"
							:loaded-accounts="accounts"
							@close="closeBulkSettings"
							@confirm="handleBulkUploadConfirm"
						/>
						<button class="group flex items-center gap-2" @click="isTtsOpen = true">
							<div class="fabLabel">
								<p>Text-to-speech</p>
							</div>
							<div
								class="fab blue h-10 w-10 min-w-10 group-hover:bg-blue-600 group-active:bg-blue-700 sm:h-12 sm:w-12 sm:min-w-12"
							>
								<span class="fa-light fa-wand-magic-sparkles"></span>
							</div>
							<tts-dialog
								v-if="isTtsOpen"
								:open="isTtsOpen"
								:selected-tenant-id="searchParams.selectedTenant"
								@close="closeAndReload"
							/>
						</button>
					</div>
				</template>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchField"
				:tenants="tenants"
				:accounts="accounts"
				:allow-tenants="true"
				tenant-label="Tenant Files"
				:show-tenant-filter="tenants?.length > 1"
				show-account-filter
				show-search-filter
				search-label="Search Name"
				@update:filters="searchParams = $event"
				@update:search-term="searchField = $event"
				@tenant-change="$global.setSelectedTenant"
			/>

			<data-view
				:view-mode="viewMode"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				custom-card
				:loading="loading"
				:label="'Files'"
				:total-items="totalFiles"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="computedPages"
				:page-options="[50, 100, 250]"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<div v-if="!loading" class="flex w-full flex-col">
						<div v-for="file in files" :key="file.id" class="card no-action listItem cursor-auto">
							<div class="row content middle-sm middle-md middle-lg">
								<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 tableOverflow">
									<div class="row middle-sm middle-md middle-lg">
										<div
											class="col-xs-12 col-sm-4 col-md-4 col-lg-4 flex flex-nowrap items-center justify-between gap-1"
										>
											<p>{{ file.name }}</p>
											<div class="flex items-center gap-2">
												<span
													class="fa-light fa-download cursor-pointer"
													@click.prevent="downloadFile(file)"
												></span>
												<span
													class="cursor-pointer"
													:class="{
														'fa-light fa-eye-slash':
															previewedFile && previewedFile.id === file.id,
														'fa-light fa-play':
															!previewedFile || previewedFile.id !== file.id
													}"
													@click="togglePreview(file)"
												></span>
											</div>
										</div>
										<div class="col-xs-12 col-sm-2 col-md-2 col-lg-2">
											<p>{{ file.type }}</p>
										</div>
										<div class="col-xs-12 col-sm-2 col-md-2 col-lg-2">
											<p>{{ formatFileSize(file?.fileInfo?.size || 0) }}</p>
										</div>
										<div class="col-xs-12 col-sm-2 col-md-2 col-lg-2">
											<p>{{ formatDate(file.createdAt) }}</p>
										</div>
										<div class="col-xs-12 col-sm-2 col-md-2 col-lg-2">
											<p>{{ formatDate(file.updatedAt) }}</p>
										</div>
									</div>
								</div>
							</div>
							<media-player v-if="previewedFile && previewedFile.id === file.id" :file="file" />
						</div>
						<div v-if="!files || !files.length" class="card no-action listItem orange">
							<div class="row content middle-sm middle-md middle-lg">
								<div class="col-xs-12 py-8 text-center">
									<div class="flex flex-col items-center gap-4">
										<div class="text-4xl text-gray-400">
											<span class="fa-light fa-file"></span>
										</div>
										<div class="flex flex-col items-center gap-2">
											<p class="text-lg font-medium text-gray-600">No files yet</p>
											<p class="text-sm text-gray-500">Upload your first file to get started.</p>
										</div>
										<button
											v-if="$global.hasUserRole(userRoles, 'files:create')"
											class="jmiBtn orange withIcon"
											@click="isUploadOpen = true"
										>
											<span class="fa-light fa-plus"></span>Upload File
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</template>

				<template #card-view>
					<div v-if="!loading" class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
						<div
							v-for="file in files"
							:key="file.id"
							class="hover truncate rounded-md border-2 border-transparent bg-white p-4 shadow-sm transition-all duration-300 dark:bg-offmaindark"
							:class="{
								'border-green': highlightItem === file.id
							}"
						>
							<div class="card-header flex items-center justify-start gap-2">
								<div class="w-4">
									<span :class="getFileIconClass(file.type)" class="text-blue"></span>
								</div>
								<p class="truncate text-sm font-bold" @mouseover="createTooltip($event, file.name)">
									{{ file.name }}
								</p>
							</div>
							<div class="card-body">
								<p class="text-sm font-light">{{ file.type || 'No Filetype' }}</p>
								<p class="text-sm font-light">Size: {{ formatFileSize(file?.fileInfo?.size || 0) }}</p>
								<div class="flex items-center justify-between gap-2">
									<p class="w-1/2 truncate text-xs font-medium text-stone-400 xs:w-auto">
										{{ formatDate(file.createdAt) }}
									</p>
									<div class="flex gap-2">
										<button @click.prevent="downloadFile(file)">
											<span class="fa-light fa-download"></span>
										</button>
										<Dialog>
											<DialogTrigger>
												<span
													class="fa-light fa-play-circle scale-100 cursor-pointer text-green"
													@click="togglePreview(file)"
												></span>
											</DialogTrigger>
											<DialogContent>
												<VisuallyHidden>
													<DialogTitle>{{ file.name }}</DialogTitle>
													<DialogDescription
														>Media playback modal for {{ file.name }}</DialogDescription
													>
												</VisuallyHidden>
												<media-player
													v-if="previewedFile && previewedFile.id === file.id"
													:file="file"
												/>
											</DialogContent>
										</Dialog>
									</div>
								</div>
							</div>
						</div>
					</div>
				</template>
			</data-view>
		</div>
	</div>
</template>

<script>
import filesApi from '@/scripts/files/files.api'
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { VisuallyHidden } from 'radix-vue'
import accountsApi from '@/scripts/accounts/accounts.api'
import cdrApi from '@/scripts/calldetailrecords/calldetailrecords.api'
import debounce from 'lodash.debounce'
import moment from 'moment'
import voicemailMessagesApi from '@/scripts/voicemailmessages/voicemailmessages.api'

export default {
	components: {
		Dialog,
		DialogTrigger,
		DialogContent,
		DialogTitle,
		DialogDescription,
		VisuallyHidden
	},
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-4 col-md-4 col-lg-4'
				},
				{
					key: 'type',
					label: 'Type',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'size',
					label: 'Size',
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'createdAt',
					label: 'Created At',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'updatedAt',
					label: 'Updated At',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				}
			],
			showFilters: true,
			searchParams: {
				selectedTenant: '',
				selectedAccount: null,
				dateAddedFrom: '',
				dateAddedTo: ''
			},
			searchField: '',
			totalFiles: 0,
			pageSize: 50,
			currentPage: 1,
			sortColumn: 'name',
			reverse: false,
			accounts: [],
			loading: false,
			files: [],
			mediaLoaded: false,
			isRecordOpen: false,
			isTtsOpen: false,
			highlightItem: null,
			isUploadOpen: false,
			isBulkSettingsOpen: false,
			pendingFiles: null,
			debouncedWatch: null,
			previewedFile: null,
			viewMode: localStorage.getItem('fileBrowserViewMode') || 'card',
			bulkUpload: {
				isUploading: false,
				progress: 0,
				total: 0,
				processed: 0,
				errors: [],
				successes: []
			}
		}
	},

	computed: {
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return ''
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || ''
		},
		computedPages() {
			const numPages = Math.ceil(this.totalFiles / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		}
	},

	watch: {
		searchParams: {
			handler(newValue, oldValue) {
				this.reload()
			},
			deep: true
		},
		searchField(newValue, oldValue) {
			this.debouncedWatch()
		},
		currentPage: {
			handler(newValue, oldValue) {
				this.reload()
			}
		}
	},

	async mounted() {
		this.reload()

		this.debouncedWatch = debounce(this.reload, 1000)

		accountsApi
			.getAll({ limit: 2500 })
			.then((response) => {
				this.accounts = response.data.results
			})
			.catch((error) => {
				console.log(error)
			})

		if (this.tenants?.length === 1) {
			this.searchParams.selectedTenant = this.tenants[0].id
			this.$global.setSelectedTenant({ target: { value: this.tenants[0].id } })
		}
	},

	methods: {
		resetSearchParams() {
			this.searchParams.selectedTenant = ''
			this.searchParams.selectedAccount = ''
			this.searchField = ''
		},

		getSearchParams() {
			let params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {}
			}

			if (this.searchParams) {
				params.where.createdAtFrom = this.searchParams.dateAddedFrom
					? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 00:00:00'
					: null
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD') + ' 23:59:59'
					: this.searchParams.dateAddedFrom
						? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 23:59:59'
						: null
			}

			if (this.searchParams.selectedTenant) params.where.selectedTenant = this.searchParams.selectedTenant
			if (this.searchParams.selectedAccount) params.where.selectedAccount = this.searchParams.selectedAccount

			if (!this.sortColumn) this.sortColumn = 'name'

			params.order = {
				[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
			}

			return params
		},

		async reload(created) {
			if (!this.loading) {
				this.loading = true
				let params = this.getSearchParams()

				if (this.searchField) {
					params.searchTerm = this.searchField
				}

				try {
					const response = await filesApi.getAll(params)

					this.loading = false
					this.totalFiles = response.data.count
					this.files = response.data.results
					this.highlightItem = created ? created.id : null
					setTimeout(() => {
						this.highlightItem = null
					}, 2000)
				} catch (error) {
					this.loading = false
				}
			}
		},

		changePageSize(size) {
			this.pageSize = size
			let numPages = Math.ceil(this.totalFiles / size) || 1
			if (numPages < this.currentPage) this.currentPage = numPages
			else this.reload()
		},

		async downloadFile(file) {
			try {
				let response
				if (file.type === 'cdr') {
					console.log('fetching cdr', file)
					response = await cdrApi.downloadById(file.id)
				} else if (file.type === 'voicemail') {
					response = await voicemailMessagesApi.downloadById(file.id)
				} else {
					response = await filesApi.downloadById(file.id)
				}
				const fileBlob = new Blob([response])
				const url = window.URL.createObjectURL(fileBlob)
				const a = document.createElement('a')
				a.style.display = 'none'
				a.href = url
				a.download = file.name
				document.body.appendChild(a)
				a.click()
				window.URL.revokeObjectURL(url)
				document.body.removeChild(a)
			} catch (error) {
				console.error(error)
			}
		},

		setSort(column, reverse) {
			this.sortColumn = column
			this.reverse = reverse
			this.reload()
		},

		async togglePreview(file) {
			if (this.previewedFile && this.previewedFile.id === file.id) {
				this.previewedFile = null
			} else {
				this.previewedFile = file
			}
		},

		formatDate(date) {
			return new Date(date).toLocaleString('en-GB')
		},

		formatFileSize(size) {
			if (size < 1024) return size + ' B'
			if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB'
			if (size < 1024 * 1024 * 1024) return (size / (1024 * 1024)).toFixed(2) + ' MB'
			if (size < 1024 * 1024 * 1024 * 1024) return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
			return 'Unknown'
		},

		async closeAndReload(result) {
			this.isUploadOpen = false
			this.isRecordOpen = false
			this.isTtsOpen = false
			await this.reload(result)
		},

		toggleViewMode() {
			const newMode = this.viewMode === 'card' ? 'table' : 'card'
			this.viewMode = newMode
			localStorage.setItem('fileBrowserViewMode', newMode)
		},

		getFileIconClass(fileType) {
			if (fileType?.startsWith('audio')) return 'fa-light fa-speaker'
			return 'fa-light fa-file'
		},

		createTooltip(event, text) {
			if (text) {
				event.target.setAttribute('title', text)
			}
		},

		changePage(page) {
			this.currentPage = page
			this.reload()
		},

		openBulkUpload() {
			this.isBulkSettingsOpen = true
		},

		closeBulkSettings() {
			this.isBulkSettingsOpen = false
			this.pendingFiles = null
		},

		handleBulkUploadConfirm(uploadSettings) {
			this.isBulkSettingsOpen = false
			this.uploadBulkFiles(uploadSettings.files, uploadSettings)
		},

		async uploadBulkFiles(files, uploadSettings) {
			this.bulkUpload = {
				isUploading: true,
				progress: 0,
				total: files.length,
				processed: 0,
				errors: [],
				successes: []
			}

			this.showBulkUploadModal()

			for (let i = 0; i < files.length; i++) {
				const file = files[i]
				try {
					const formData = new FormData()
					formData.append('file', file)

					await filesApi[uploadSettings.selectedAccount ? 'createAccountFile' : 'createTenantFile'](
						uploadSettings.selectedAccount || uploadSettings.selectedTenant,
						formData
					)
					this.bulkUpload.successes.push(file.name)
				} catch (error) {
					const errorMessage = error.response?.data?.error || 'Unknown error'
					this.bulkUpload.errors.push({ file: file.name, error: errorMessage })
				}

				this.bulkUpload.processed++
				this.bulkUpload.progress = Math.round((this.bulkUpload.processed / this.bulkUpload.total) * 100)

				await new Promise((resolve) => setTimeout(resolve, 100))
			}

			this.showUploadSummary()
			this.bulkUpload.isUploading = false
			this.pendingFiles = null
			await this.reload()
		},

		showBulkUploadModal() {
			this.$swal({
				title: 'Processing Upload',
				html: `
					<div class="text-left">
						<p class="mb-4">Processing ${this.bulkUpload.total} files...</p>
						<div class="mb-2">
							<div class="bg-gray-200 rounded-full h-2">
								<div id="progress-bar" class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
							</div>
						</div>
						<p id="progress-text" class="text-sm text-gray-600">0 of ${this.bulkUpload.total} processed</p>
					</div>
				`,
				allowOutsideClick: false,
				showConfirmButton: false,
				didOpen: () => {
					const updateProgress = () => {
						const progressBar = document.getElementById('progress-bar')
						const progressText = document.getElementById('progress-text')
						if (progressBar && progressText) {
							progressBar.style.width = `${this.bulkUpload.progress}%`
							progressText.textContent = `${this.bulkUpload.processed} of ${this.bulkUpload.total} processed`
						}
						if (this.bulkUpload.isUploading) {
							setTimeout(updateProgress, 100)
						}
					}
					updateProgress()
				}
			})
		},

		showUploadSummary() {
			const { successes, errors, total } = this.bulkUpload
			let html = `<div class="text-left">`

			if (successes.length > 0) {
				html += `<p class="text-green-600 mb-2"><strong>✓ ${successes.length} files uploaded successfully</strong></p>`
			}

			if (errors.length > 0) {
				html += `<p class="text-red-600 mb-2"><strong>✗ ${errors.length} files failed</strong></p>`
				html += `<div class="max-h-40 overflow-y-auto text-sm">`
				errors.forEach(({ file, error }) => {
					html += `<p class="text-red-500">• ${file}: ${error}</p>`
				})
				html += `</div>`
			}

			html += `</div>`

			this.$swal({
				title: 'Upload Complete',
				html,
				icon: errors.length === 0 ? 'success' : successes.length === 0 ? 'error' : 'warning',
				confirmButtonText: 'OK'
			})
		}
	}
}
</script>
